import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import {
  trackDiagnosticComplete,
  trackDiagnosticStart,
  trackWhatsappClick,
} from "@/lib/analytics";
import { diagnosticOptions } from "@/lib/commercial-content";
import { buildWhatsappLink } from "@/lib/constants";

const initialForm = {
  goal: diagnosticOptions[0],
  name: "",
  company: "",
  website: "",
  email: "",
  phone: "",
  problem: "",
};

const DiagnosticPage = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const markStarted = () => {
    if (!started) {
      trackDiagnosticStart("diagnostic_page");
      setStarted(true);
    }
  };

  const updateField = (field) => (event) => {
    markStarted();
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const message = [
    "Olá! Quero receber um diagnóstico digital gratuito da BezierClick.",
    `Objetivo: ${form.goal || "-"}`,
    `Nome: ${form.name || "-"}`,
    `Empresa: ${form.company || "-"}`,
    `Website atual: ${form.website || "-"}`,
    `Email: ${form.email || "-"}`,
    `Telefone: ${form.phone || "-"}`,
    `Problema: ${form.problem || "-"}`,
  ].join("\n");
  const whatsappHref = buildWhatsappLink(message, "diagnostic_form");

  const handleSubmit = (event) => {
    event.preventDefault();
    trackDiagnosticComplete("diagnostic_page");
    trackWhatsappClick("diagnostic_form");
    setSubmitted(true);
    window.open(whatsappHref, "_blank", "noopener,noreferrer");
  };

  return (
    <main id="main-content" className="bg-[#0a0a0a] pt-24">
      <section className="bg-[#ff6b35] noise py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(320px,1.1fr)] gap-8 lg:gap-12 items-start">
            <div>
              <div className="inline-block bg-[#0a0a0a] text-white px-3 py-1 brutal-border font-mono text-xs uppercase tracking-widest mb-5">
                Diagnóstico Digital Gratuito
              </div>
              <h1 className="font-display text-[#0a0a0a] text-[2.65rem] min-[380px]:text-5xl sm:text-7xl lg:text-8xl uppercase leading-[0.9]">
                Descubra onde a sua empresa pode melhorar
              </h1>
              <p className="mt-6 max-w-2xl text-[#0a0a0a]/80 text-lg sm:text-xl leading-snug">
                Responda a algumas perguntas. Analisamos a sua presença e processos digitais
                e indicamos quais devem ser as prioridades.
              </p>
              <p className="mt-5 inline-block bg-white brutal-border px-4 py-3 font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                Gratuito · Sem compromisso · Análise personalizada · 3 minutos
              </p>
            </div>

            <form className="brutal-card bg-white p-5 sm:p-7" onSubmit={handleSubmit}>
              {submitted ? (
                <div className="min-h-[420px] flex flex-col justify-center">
                  <CheckCircle2 size={46} className="text-[#ff6b35] mb-5" />
                  <h2 className="font-display text-[#0a0a0a] text-4xl uppercase leading-none">
                    Diagnóstico recebido
                  </h2>
                  <p className="mt-4 text-[#0a0a0a]/75 leading-snug">
                    A conversa foi aberta no WhatsApp com o contexto organizado. Vamos analisar
                    o problema e responder com o melhor caminho.
                  </p>
                </div>
              ) : (
                <>
                  <div className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-5">
                    Passo {step} de 2
                  </div>

                  {step === 1 ? (
                    <div>
                      <h2 className="font-display text-[#0a0a0a] text-4xl uppercase leading-none mb-6">
                        O que pretende melhorar?
                      </h2>
                      <div className="grid grid-cols-1 gap-3">
                        {diagnosticOptions.map((option) => (
                          <label
                            key={option}
                            className="flex items-center gap-3 brutal-border px-4 py-3 cursor-pointer hover:bg-[#ff6b35]"
                          >
                            <input
                              type="radio"
                              name="goal"
                              value={option}
                              checked={form.goal === option}
                              onChange={updateField("goal")}
                              className="h-5 w-5 accent-[#ff6b35]"
                            />
                            <span className="text-[#0a0a0a] font-medium">{option}</span>
                          </label>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          markStarted();
                          setStep(2);
                        }}
                        className="btn-brutal btn-brutal-orange w-full mt-6"
                      >
                        <span>Continuar</span>
                        <ArrowRight size={18} className="shrink-0" />
                      </button>
                      <p className="mt-4 font-mono text-[0.68rem] uppercase tracking-widest text-[#0a0a0a]/60 leading-relaxed">
                        O diagnóstico tem 2 passos e demora cerca de 3 minutos.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <label className="block">
                          <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">Nome</span>
                          <input type="text" name="name" value={form.name} onChange={updateField("name")} autoComplete="name" className="mt-2 w-full bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]" />
                        </label>
                        <label className="block">
                          <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">Empresa</span>
                          <input type="text" name="company" value={form.company} onChange={updateField("company")} autoComplete="organization" className="mt-2 w-full bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]" />
                        </label>
                      </div>
                      <label className="block">
                        <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">Website atual</span>
                        <input type="url" name="website" value={form.website} onChange={updateField("website")} autoComplete="url" placeholder="https://…" className="mt-2 w-full bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]" />
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <label className="block">
                          <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">Email</span>
                          <input type="email" name="email" value={form.email} onChange={updateField("email")} autoComplete="email" spellCheck={false} className="mt-2 w-full bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]" />
                        </label>
                        <label className="block">
                          <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">Telefone</span>
                          <input type="tel" name="phone" value={form.phone} onChange={updateField("phone")} autoComplete="tel" inputMode="tel" className="mt-2 w-full bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]" />
                        </label>
                      </div>
                      <label className="block">
                        <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">Descrição do problema</span>
                        <textarea name="problem" value={form.problem} onChange={updateField("problem")} rows={5} maxLength={900} className="mt-2 w-full resize-y bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]" />
                      </label>
                      <button type="submit" className="btn-brutal btn-brutal-orange w-full">
                        <span>Fazer diagnóstico gratuito</span>
                        <ArrowRight size={18} className="shrink-0" />
                      </button>
                      <p className="font-mono text-[0.68rem] uppercase tracking-widest text-[#0a0a0a]/60 leading-relaxed">
                        Não enviamos dados pessoais para Analytics. A mensagem é enviada
                        apenas quando abrir a conversa no WhatsApp.
                      </p>
                    </div>
                  )}
                </>
              )}
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DiagnosticPage;
